import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, Mail, Phone, Eye, RefreshCw, Search, CalendarIcon, X, Download } from "lucide-react";

interface MaterialsLead {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  interest_type: string | null;
  message: string | null;
  status: string | null;
  qualification_score: number | null;
  created_at: string;
}

const statusColor = (s: string | null) => {
  if (s === "contacted") return "default";
  if (s === "qualified") return "secondary";
  return "outline";
};

export const MaterialsQuotesPanel = () => {
  const [leads, setLeads] = useState<MaterialsLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<MaterialsLead | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  const fetchLeads = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .ilike("interest_type", "%Materials Quote%")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Failed to load Materials Quotes");
    } else {
      setLeads((data as MaterialsLead[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const markContacted = async (id: string) => {
    setUpdating(id);
    const { error } = await supabase
      .from("leads")
      .update({ status: "contacted", updated_at: new Date().toISOString() })
      .eq("id", id);
    setUpdating(null);
    if (error) {
      toast.error("Could not update lead");
      return;
    }
    toast.success("Lead marked as contacted");
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status: "contacted" } : l)));
    if (selected?.id === id) setSelected({ ...selected, status: "contacted" });
  };

  const reopen = async (id: string) => {
    setUpdating(id);
    const { error } = await supabase
      .from("leads")
      .update({ status: "new", updated_at: new Date().toISOString() })
      .eq("id", id);
    setUpdating(null);
    if (error) {
      toast.error("Could not update lead");
      return;
    }
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status: "new" } : l)));
    if (selected?.id === id) setSelected({ ...selected, status: "new" });
  };

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "all" && (l.status || "new") !== statusFilter) return false;
      if (q) {
        const hay = `${l.name ?? ""} ${l.email ?? ""} ${l.company ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (dateFrom) {
        if (new Date(l.created_at) < new Date(dateFrom.setHours(0, 0, 0, 0))) return false;
      }
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        if (new Date(l.created_at) > end) return false;
      }
      return true;
    });
  }, [leads, search, statusFilter, dateFrom, dateTo]);

  const pending = filteredLeads.filter((l) => l.status !== "contacted").length;
  const hasFilters = !!(search || statusFilter !== "all" || dateFrom || dateTo);
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const exportCSV = () => {
    if (filteredLeads.length === 0) {
      toast.error("No leads to export");
      return;
    }
    const headers = ["Date", "Name", "Email", "Phone", "Company", "Status", "Score", "Interest", "Message"];
    const escape = (v: any) => {
      const s = (v ?? "").toString().replace(/"/g, '""');
      return `"${s}"`;
    };
    const rows = filteredLeads.map((l) => [
      new Date(l.created_at).toISOString(),
      l.name,
      l.email,
      l.phone,
      l.company,
      l.status || "new",
      l.qualification_score ?? 0,
      l.interest_type,
      l.message,
    ].map(escape).join(","));
    const csv = [headers.map(escape).join(","), ...rows].join("\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `materials-quotes-${format(new Date(), "yyyy-MM-dd-HHmm")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredLeads.length} lead${filteredLeads.length === 1 ? "" : "s"}`);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="font-serif">Materials Quote Submissions</CardTitle>
          <CardDescription>
            Showing {filteredLeads.length} of {leads.length} · {pending} awaiting follow-up
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={filteredLeads.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchLeads} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name, email, company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("justify-start font-normal", !dateFrom && "text-muted-foreground")}>
                <CalendarIcon className="h-4 w-4 mr-2" />
                {dateFrom ? format(dateFrom, "PP") : "From date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus className={cn("p-3 pointer-events-auto")} />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("justify-start font-normal", !dateTo && "text-muted-foreground")}>
                <CalendarIcon className="h-4 w-4 mr-2" />
                {dateTo ? format(dateTo, "PP") : "To date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus className={cn("p-3 pointer-events-auto")} />
            </PopoverContent>
          </Popover>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-center py-8 text-muted-foreground">Loading…</p>
        ) : filteredLeads.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">
            {leads.length === 0 ? "No Materials Quote requests yet." : "No results match the current filters."}
          </p>
        ) : (
          <div className="space-y-3">
            {filteredLeads.map((lead) => (
              <div
                key={lead.id}
                className="flex flex-col md:flex-row md:items-center justify-between gap-3 border rounded-lg p-4 hover:bg-muted/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-semibold">{lead.name || "Anonymous"}</span>
                    <Badge variant={statusColor(lead.status) as any}>{lead.status || "new"}</Badge>
                    <Badge variant="outline">Score {lead.qualification_score ?? 0}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(lead.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground flex flex-wrap gap-3">
                    {lead.email && (
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-primary">
                        <Mail className="h-3 w-3" /> {lead.email}
                      </a>
                    )}
                    {lead.phone && (
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-primary">
                        <Phone className="h-3 w-3" /> {lead.phone}
                      </a>
                    )}
                    {lead.company && <span>· {lead.company}</span>}
                  </div>
                  <p className="text-xs mt-1 text-muted-foreground truncate">{lead.interest_type}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setSelected(lead)}>
                    <Eye className="h-4 w-4 mr-1" /> View
                  </Button>
                  {lead.status === "contacted" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => reopen(lead.id)}
                      disabled={updating === lead.id}
                    >
                      Reopen
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => markContacted(lead.id)}
                      disabled={updating === lead.id}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Mark contacted
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {selected?.name || "Materials Quote"}
            </DialogTitle>
            <DialogDescription>
              Submitted {selected && new Date(selected.created_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground">Email:</span> {selected.email || "—"}</div>
                <div><span className="text-muted-foreground">Phone:</span> {selected.phone || "—"}</div>
                <div><span className="text-muted-foreground">Company:</span> {selected.company || "—"}</div>
                <div><span className="text-muted-foreground">Status:</span> {selected.status || "new"}</div>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Interest type</p>
                <p>{selected.interest_type}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Submission summary</p>
                <pre className="whitespace-pre-wrap bg-muted/40 rounded-md p-3 text-xs leading-relaxed font-sans">
                  {selected.message || "(no message)"}
                </pre>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {selected.email && (
                  <Button asChild variant="outline">
                    <a href={`mailto:${selected.email}`}>
                      <Mail className="h-4 w-4 mr-1" /> Reply
                    </a>
                  </Button>
                )}
                {selected.status !== "contacted" && (
                  <Button onClick={() => markContacted(selected.id)} disabled={updating === selected.id}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Mark contacted
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MaterialsQuotesPanel;
